import React from 'react';

// Map human car-color names → { body, shade } hex tones for the cartoon car.
const COLOR_MAP = {
  silver: { body: '#C7CBD0', shade: '#A6ABB2' },
  grey: { body: '#B4B8BD', shade: '#94989E' },
  gray: { body: '#B4B8BD', shade: '#94989E' },
  white: { body: '#EDEFF2', shade: '#CBD0D6' },
  black: { body: '#33383D', shade: '#202427' },
  red: { body: '#DC4030', shade: '#B22C20' },
  maroon: { body: '#8E2B24', shade: '#6E1F1A' },
  blue: { body: '#2F6FD8', shade: '#245AB0' },
  navy: { body: '#27407A', shade: '#1C3160' },
  green: { body: '#2E9E5B', shade: '#237B47' },
  orange: { body: '#E8722C', shade: '#C25B1F' },
  yellow: { body: '#E8C22C', shade: '#C7A31E' },
  gold: { body: '#C9A24B', shade: '#A9873A' },
  brown: { body: '#7A5230', shade: '#5E3E23' },
  beige: { body: '#D8C9A8', shade: '#B9A882' },
  purple: { body: '#7E4FC0', shade: '#653D9C' },
};

const resolveTone = (colorName) => {
  if (!colorName) return { body: '#6B7280', shade: '#4B5563' };
  const key = String(colorName).trim().toLowerCase();
  // match on the first known token (e.g. "dark silver" → silver)
  const found = Object.keys(COLOR_MAP).find((c) => key.includes(c));
  return found ? COLOR_MAP[found] : { body: '#6B7280', shade: '#4B5563' };
};

/**
 * Side-view cartoon car (hatchback style) tinted to the driver's car colour.
 * Scales fluidly to its container width.
 */
const CartoonCar = ({ color, className = '', title }) => {
  const { body, shade } = resolveTone(color);
  return (
    <svg
      viewBox="0 0 220 110"
      className={className}
      role="img"
      aria-label={title || `${color || 'Vehicle'} car`}
      style={{ width: '100%', height: 'auto' }}
    >
      <title>{title || `${color || 'Vehicle'} car`}</title>
      {/* ground shadow */}
      <ellipse cx="110" cy="97" rx="86" ry="8" fill="#000000" opacity="0.12" />

      {/* lower body */}
      <path
        d="M14 74 C14 66 20 62 30 60 L44 44 C50 36 60 32 74 32 L150 32 C168 32 182 40 192 54 L200 62 C206 64 208 68 208 74 L208 82 C208 86 205 88 201 88 L21 88 C17 88 14 86 14 82 Z"
        fill={body}
      />
      {/* darker skirt */}
      <path
        d="M14 78 L208 78 L208 82 C208 86 205 88 201 88 L21 88 C17 88 14 86 14 82 Z"
        fill={shade}
      />

      {/* roof / cabin */}
      <path
        d="M52 42 C58 35 66 33 76 33 L138 33 C154 33 166 40 176 52 L64 52 C57 52 52 48 52 42 Z"
        fill={shade}
        opacity="0.55"
      />

      {/* windows */}
      <path d="M70 37 L96 37 L96 51 L60 51 C60 45 64 39 70 37 Z" fill="#BFE0F5" opacity="0.9" />
      <path d="M102 37 L134 37 C146 38 156 44 164 51 L102 51 Z" fill="#BFE0F5" opacity="0.9" />
      {/* window divider */}
      <rect x="97" y="37" width="4" height="15" fill={shade} opacity="0.7" />

      {/* door line + handle */}
      <path d="M110 54 L110 82" stroke={shade} strokeWidth="2" opacity="0.5" />
      <rect x="118" y="62" width="12" height="3" rx="1.5" fill={shade} opacity="0.7" />

      {/* headlight + taillight */}
      <path d="M200 62 C204 63 206 66 206 70 L198 70 L196 63 Z" fill="#FFE9A8" />
      <rect x="16" y="66" width="7" height="8" rx="2" fill="#E5484D" opacity="0.85" />

      {/* wheels */}
      <g>
        <circle cx="62" cy="86" r="16" fill="#26292E" />
        <circle cx="62" cy="86" r="7.5" fill="#C9CDD2" />
        <circle cx="62" cy="86" r="3" fill="#8A8F96" />
      </g>
      <g>
        <circle cx="162" cy="86" r="16" fill="#26292E" />
        <circle cx="162" cy="86" r="7.5" fill="#C9CDD2" />
        <circle cx="162" cy="86" r="3" fill="#8A8F96" />
      </g>

      {/* top highlight */}
      <path
        d="M60 40 C70 35 130 35 168 50"
        stroke="#FFFFFF"
        strokeWidth="2"
        fill="none"
        opacity="0.35"
        strokeLinecap="round"
      />
    </svg>
  );
};

export default CartoonCar;
